import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/utils/dateLocale';
import { isCompleted, isLive, isPreMatch, STATUS_CSS_CLASS, STATUS_BADGE_TEXT } from '@/constants/matchStatus';
import { formatPlaceholderTeam } from '@/utils/placeholderTeam';
import { formatLiveMinute } from '@/utils/liveMinute';
import { formatPoints } from '@/utils/formatPoints';
import { useLiveTick } from '@/hooks/useLiveTick';
import styles from './styles.module.scss';

interface Coefficients {
  team1Win?: number | string | null;
  draw?: number | string | null;
  team2Win?: number | string | null;
  source?: string;
  team1Bookmaker?: string | null;
  drawBookmaker?: string | null;
  team2Bookmaker?: string | null;
}

interface MatchDetailCardProps {
  match: {
    id: number;
    team1: string;
    team2: string;
    team1_short?: string;
    team2_short?: string;
    team1_logo_url?: string;
    team2_logo_url?: string;
    /**
     * Knockout placeholder strings (e.g. "Winner Group A", "Winner R16-1") for
     * matches whose teams haven't been determined yet. When `team1` is empty
     * AND `team1_placeholder` is set, the placeholder is rendered in the same
     * slot. Same contract as `MatchPreviewCard.team1Placeholder`.
     */
    team1_placeholder?: string | null;
    team2_placeholder?: string | null;
    match_date: string;
    status: string;
    score_team1?: number | null;
    score_team2?: number | null;
    venue?: string;
    tournament_name?: string;
    team1_win_coefficient?: number | null;
    team2_win_coefficient?: number | null;
    draw_coefficient?: number | null;
    elapsed_minutes?: number | null;
    elapsed_extra_minutes?: number | null;
    elapsed_updated_at?: string | null;
  };
  userPrediction?: {
    predicted_score_team1: number;
    predicted_score_team2: number;
    /** Exact-score coefficient locked at prediction time — shown in the prediction strip
     *  while the match is not yet finished. */
    odds_score?: number | string | null;
    points_earned?: number | null;
  } | null;
  /** Coefficients data — when provided, renders a coefficients strip. */
  coefficients?: Coefficients;
  /** Whether the user can edit their prediction (drives the pencil / "+" affordance). */
  canEditPrediction?: boolean;
  /** Fired when user taps the pencil (edit) or the empty-state "+ Сделать прогноз" CTA. */
  onEditPrediction?: () => void;
  /** Whether the user can edit coefficients (drives the pencil / "+" affordance on the coefficients strip). */
  canEditCoefficients?: boolean;
  /** Fired when user taps the pencil (edit) or the empty-state "+ Задать коэффициенты" CTA. */
  onEditCoefficients?: () => void;
  /** Active form in the unified takeover layout. Drives strip swapping. */
  activeForm?: 'none' | 'prediction' | 'coefficients';
  /** Element rendered in place of the prediction strip when activeForm === 'prediction'. Owned by parent — typically `<PredictionCard noChrome initialEditMode />`. */
  predictionFormSlot?: React.ReactNode;
  /** Element rendered in place of the coefficients strip when activeForm === 'coefficients'. */
  coefficientsFormSlot?: React.ReactNode;
  className?: string;
}

/**
 * Hero match card for the match-detail page. Single-row canonical layout
 * (same grammar as MatchPreviewCard, scaled up for hero role):
 *   [team1 name + logo] · [score or kickoff time + LIVE pill] · [logo + team2 name]
 *
 * On mobile/tablet (`<lg`) DetailsTab passes `coefficients`, `canEdit*`,
 * `activeForm`, and form slots — the card then renders 4 strips top-to-bottom:
 *   1. hero
 *   2. meta (venue + status badge)
 *   3. prediction footer (display + pencil OR empty-state CTA OR form slot)
 *   4. coefficients footer (display + pencil OR empty-state CTA OR form slot)
 * When a form is active, the meta strip and the non-active footer are hidden —
 * the active form occupies that area (mirroring `pages/account/SecurityTab`'s
 * sibling-hide takeover pattern).
 *
 * On desktop (`>=lg`) the sidebar owns PredictionCard + CoefficientsCard, so
 * DetailsTab omits the unified-mode props and only hero + meta render.
 */
export const MatchDetailCard: React.FC<MatchDetailCardProps> = ({
  match,
  userPrediction,
  coefficients,
  canEditPrediction = false,
  onEditPrediction,
  canEditCoefficients = false,
  onEditCoefficients,
  activeForm = 'none',
  predictionFormSlot,
  coefficientsFormSlot,
  className,
}) => {
  const { t } = useTranslation(['tournament']);
  const matchDate = new Date(match.match_date);
  const isFinished = isCompleted(match.status);
  const isLiveMatch = isLive(match.status);
  const time = format(matchDate, 'HH:mm', { locale: getDateLocale() });
  // Subscribe to the live ticker only when this hero card is showing a live
  // match — match-detail page only renders one card, so the perf concern is
  // smaller than on the list, but keeping the gate consistent across cards.
  const nowMs = useLiveTick(isLiveMatch);
  const liveMinuteText = isLiveMatch
    ? formatLiveMinute({
        status: match.status,
        elapsedMinutes: match.elapsed_minutes,
        elapsedExtraMinutes: match.elapsed_extra_minutes,
        elapsedUpdatedAt: match.elapsed_updated_at,
        nowMs,
      })
    : null;
  const liveBadgeLabel = isLiveMatch ? (liveMinuteText ?? 'LIVE') : null;

  // Display-name resolution mirrors MatchPreviewCard: real team name wins;
  // when missing/empty, fall back to the localised placeholder (e.g.
  // "Winner Group A" → "Победитель группы A"); final fallback is "TBD".
  // `isPlaceholderTeamN` gates logo rendering — placeholders never carry a
  // real logo, so the image slot collapses to its blank box.
  const trimmedTeam1 = match.team1?.trim() || '';
  const trimmedTeam2 = match.team2?.trim() || '';
  const trimmedPlaceholder1 = formatPlaceholderTeam(match.team1_placeholder, t);
  const trimmedPlaceholder2 = formatPlaceholderTeam(match.team2_placeholder, t);
  const isPlaceholderTeam1 = !trimmedTeam1 && !!trimmedPlaceholder1;
  const isPlaceholderTeam2 = !trimmedTeam2 && !!trimmedPlaceholder2;
  const team1Name = trimmedTeam1 || trimmedPlaceholder1 || t('tournament:tbd');
  const team2Name = trimmedTeam2 || trimmedPlaceholder2 || t('tournament:tbd');

  const score1 = match.score_team1 ?? null;
  const score2 = match.score_team2 ?? null;
  const hasScore = score1 !== null && score2 !== null;

  const venueLine = match.venue || format(matchDate, 'dd MMM yyyy', { locale: getDateLocale() });

  // Coefficient-emptiness check — mirrors CoefficientsCard's "1/1.0 means not set" treatment
  const isCoeffEmpty = (val: number | string | null | undefined): boolean => {
    if (val === undefined || val === null) return true;
    const n = parseFloat(String(val));
    return isNaN(n) || n <= 1;
  };
  const hasAnyCoefficient = !!coefficients && (
    !isCoeffEmpty(coefficients.team1Win) ||
    !isCoeffEmpty(coefficients.draw) ||
    !isCoeffEmpty(coefficients.team2Win)
  );

  // Single-bookmaker pill: when all 3 odds come from the same `api` bookmaker, collapse to one pill
  const apiBookmakerCommon = coefficients?.source === 'api'
    ? [coefficients.team1Bookmaker, coefficients.drawBookmaker, coefficients.team2Bookmaker].filter(Boolean).find(b => !!b) || null
    : null;

  const ariaParts: string[] = [team1Name];
  ariaParts.push(hasScore ? `${score1}:${score2}` : time);
  ariaParts.push(team2Name);
  if (venueLine) ariaParts.push(venueLine);
  ariaParts.push(STATUS_BADGE_TEXT[match.status] || match.status);
  const ariaLabel = ariaParts.filter(Boolean).join(', ');

  // Strip visibility — when a form is active, meta + the OTHER footer hide
  // (only hero + the active form remain — SecurityTab takeover pattern).
  const showMeta = activeForm === 'none';
  const showPredictionStrip = activeForm !== 'coefficients';
  const showCoefficientsStrip = activeForm !== 'prediction';

  const predictionMode: 'form' | 'display' | 'cta' | 'hidden' =
    activeForm === 'prediction' ? 'form'
    : userPrediction ? 'display'
    : canEditPrediction ? 'cta'
    : 'hidden';

  const coefficientsMode: 'form' | 'display' | 'cta' | 'hidden' =
    activeForm === 'coefficients' ? 'form'
    : hasAnyCoefficient ? 'display'
    : canEditCoefficients ? 'cta'
    : 'hidden';

  const pointsEarned = userPrediction?.points_earned;
  const showPoints = isFinished && pointsEarned !== null && pointsEarned !== undefined;
  const pointsNum = showPoints ? parseFloat(String(pointsEarned)) : 0;

  // Pre-finish: surface the exact-score coefficient locked at prediction time
  // (no outcome coefficient, no label — just the number). Mirrors the project
  // rule "> 1 means real coefficient" used by isCoeffEmpty above.
  const oddsScoreRaw = userPrediction?.odds_score;
  const oddsScoreNum = oddsScoreRaw == null
    ? null
    : (typeof oddsScoreRaw === 'number' ? oddsScoreRaw : parseFloat(String(oddsScoreRaw)));
  const showOddsScore = !isFinished
    && oddsScoreNum !== null
    && Number.isFinite(oddsScoreNum)
    && oddsScoreNum > 1;

  return (
    <div
      className={`${styles.card} ${isLiveMatch ? styles.cardLive : ''} ${className || ''}`}
      aria-label={ariaLabel}
    >
      <div className={styles.main}>
        {/* Team 1: name (right-aligned) + logo */}
        <div className={`${styles.team} ${styles.team1}`}>
          <h3 className={styles.teamName}>{team1Name}</h3>
          {match.team1_logo_url && !isPlaceholderTeam1 ? (
            <img src={match.team1_logo_url} alt={team1Name} className={styles.logo} />
          ) : (
            <div className={styles.logoPlaceholder} aria-hidden="true" />
          )}
        </div>

        {/* Center: score or kickoff time. LIVE state is communicated by the
            pulsing badge in the footer (right of venue). */}
        <div className={styles.center}>
          {hasScore ? (
            <div className={styles.score}>
              <span>{score1}</span>
              <span className={styles.scoreSep}>:</span>
              <span>{score2}</span>
            </div>
          ) : (
            <div className={styles.kickoff}>{time}</div>
          )}
        </div>

        {/* Team 2: logo + name (left-aligned) */}
        <div className={`${styles.team} ${styles.team2}`}>
          {match.team2_logo_url && !isPlaceholderTeam2 ? (
            <img src={match.team2_logo_url} alt={team2Name} className={styles.logo} />
          ) : (
            <div className={styles.logoPlaceholder} aria-hidden="true" />
          )}
          <h3 className={styles.teamName}>{team2Name}</h3>
        </div>
      </div>

      {/* Meta: venue (left) + status badge (right). For LIVE matches the badge
          renders as a pulsing red "LIVE" pill in the same right-aligned slot. */}
      {showMeta && (
        <div className={styles.footer}>
          <span className={styles.venue}>{venueLine}</span>
          <span className={`${styles.statusBadge} ${styles[STATUS_CSS_CLASS[match.status] || 'scheduled']}`}>
            {isLiveMatch
              ? liveBadgeLabel
              : isPreMatch(match.status)
                ? time
                : (STATUS_BADGE_TEXT[match.status] || match.status)}
          </span>
        </div>
      )}

      {/* Prediction strip */}
      {showPredictionStrip && predictionMode === 'form' && (
        <div className={styles.formSlot}>{predictionFormSlot}</div>
      )}
      {showPredictionStrip && predictionMode === 'display' && userPrediction && (
        <div className={styles.predictionFooter}>
          <div className={styles.predictionInfo}>
            <span className={styles.predictionLabel}>{t('tournament:yourPrediction')}</span>
            <span className={styles.predictionScore}>
              {userPrediction.predicted_score_team1} : {userPrediction.predicted_score_team2}
            </span>
          </div>
          <div className={styles.predictionRight}>
            {showPoints && (
              <div className={styles.predictionPoints}>
                {pointsNum > 0 && (
                  <i className={`icon-chevrons-up ${styles.pointsPositive}`} />
                )}
                <span className="h6 label">
                  {pointsNum > 0 ? '+' : ''}{formatPoints(pointsNum)} pts
                </span>
              </div>
            )}
            {showOddsScore && oddsScoreNum !== null && (
              <span
                className={styles.scoreCoefficient}
                aria-label={t('tournament:potentialPointsAria', {
                  score: oddsScoreNum.toFixed(2),
                  outcome: '—',
                })}
              >
                {oddsScoreNum.toFixed(2)}
              </span>
            )}
            {canEditPrediction && onEditPrediction && (
              <button
                type="button"
                className={styles.editIconBtn}
                onClick={onEditPrediction}
                aria-label={t('tournament:editPrediction')}
              >
                <i className="icon-pen" />
              </button>
            )}
          </div>
        </div>
      )}
      {showPredictionStrip && predictionMode === 'cta' && onEditPrediction && (
        <button
          type="button"
          className={styles.addCta}
          onClick={onEditPrediction}
        >
          <span className={styles.addCtaText}>
            <i className="icon-plus" />
            {t('tournament:makePrediction')}
          </span>
        </button>
      )}

      {/* Coefficients strip */}
      {showCoefficientsStrip && coefficientsMode === 'form' && (
        <div className={styles.formSlot}>{coefficientsFormSlot}</div>
      )}
      {showCoefficientsStrip && coefficientsMode === 'display' && coefficients && (
        <div className={styles.coefficientsFooter}>
          {/* Header rendered only when there's something actionable on the right —
              the 3-cell grid below identifies itself as coefficients via its content,
              so we drop the redundant "Коэффициенты" caption on mobile. */}
          {(apiBookmakerCommon || (canEditCoefficients && onEditCoefficients)) && (
            <div className={styles.coefficientsHeader}>
              {apiBookmakerCommon && <span className={styles.sourcePill}>{apiBookmakerCommon}</span>}
              {canEditCoefficients && onEditCoefficients && (
                <button
                  type="button"
                  className={styles.editIconBtn}
                  onClick={onEditCoefficients}
                  aria-label={t('tournament:editCoefficients')}
                >
                  <i className="icon-pen" />
                </button>
              )}
            </div>
          )}
          <div className={styles.coefficientsGrid}>
            <div className={styles.coefficientItem}>
              <span className={styles.coefficientItemLabel}>{team1Name}</span>
              <span className={styles.coefficientItemValue}>
                {isCoeffEmpty(coefficients.team1Win) ? '—' : coefficients.team1Win}
              </span>
            </div>
            <div className={styles.coefficientItem}>
              <span className={styles.coefficientItemLabel}>{t('tournament:draw')}</span>
              <span className={styles.coefficientItemValue}>
                {isCoeffEmpty(coefficients.draw) ? '—' : coefficients.draw}
              </span>
            </div>
            <div className={styles.coefficientItem}>
              <span className={styles.coefficientItemLabel}>{team2Name}</span>
              <span className={styles.coefficientItemValue}>
                {isCoeffEmpty(coefficients.team2Win) ? '—' : coefficients.team2Win}
              </span>
            </div>
          </div>
        </div>
      )}
      {showCoefficientsStrip && coefficientsMode === 'cta' && onEditCoefficients && (
        <button
          type="button"
          className={styles.addCta}
          onClick={onEditCoefficients}
        >
          <span className={styles.addCtaText}>
            <i className="icon-plus" />
            {t('tournament:setCoefficientsCta')}
          </span>
        </button>
      )}
    </div>
  );
};

export default MatchDetailCard;
